import { AxiosInstance } from "@careinfo/axios-service";
import { type AxiosInstance as Instance } from "axios";
import { FieldValues, UseFormSetError } from "react-hook-form";
import { ParseError } from "./ParseError";
import { accessToken } from "./sessionKey";

let instance: Instance;

export const API_ROOT = "https://prodenvservice.careinfosolutions.com";

// Initialize the instance before making any API calls
const initInstance = async () => {
  if (!instance) {
    instance = await AxiosInstance();
  }
};

// Get data from API
const getApiData = async (
  url: string,
  params: Record<string, any> | null = null
) => {
  await initInstance();
  try {
    const response = await instance({
      method: "GET",
      url: `${url}`,
      params: params,
      transformResponse: [
        function (responseData: any) {
          try {
            return JSON.parse(responseData);
          } catch (e) {
            throw new ParseError(e?.message);
          }
        },
      ],
    });
    return response;
  } catch (e) {
    throw e;
  }
};

// Post data to API
const postApiData = async ({
  url,
  formData,
  setError,
}: {
  url: string;
  formData: any;
  setError?: any;
}) => {
  await initInstance();
  try {
    const response = await instance({
      method: "POST",
      url: `${url}`,
      data: formData,
      headers: {
        Authorization: `JWT ${accessToken()}`,
      },
      transformResponse: [
        function (responseData: any) {
          return JSON.parse(responseData);
        },
      ],
    });
    return response;
  } catch (e) {
    throw e;
  }
};

// Update data
const patchApiData = async <T>({
  url,
  formData,
  setError = null,
}: {
  url: string;
  formData: any;
  setError?: any;
}) => {
  await initInstance();
  try {
    return await instance({
      method: "PATCH",
      url: `${url}`,
      data: formData,
      headers: {
        Authorization: `JWT ${accessToken()}`,
      },
      transformResponse: [
        function (responseData: any) {
          return JSON.parse(responseData);
        },
      ],
    });
  } catch (e) {
    throw e;
  }
};

const deleteApiData = async ({
  url,
  params,
}: {
  url: string;
  params?: string;
}) => {
  await initInstance();
  try {
    return await instance({
      method: "DELETE",
      url: `${url}`,
      params: params,
    });
  } catch (e) {
    throw e;
  }
};

export const patchApiFormData = async <T>(data: {
  url: string;
  formData: FormData;
  setError: UseFormSetError<T>;
}) => {
  const { url, formData, setError } = data;
  let response: any;
  await initInstance();
  try {
    response = await instance({
      method: "PATCH",
      url: `${url}`,
      data: formData,
      headers: {
        "Content-Type": `multipart/form-data`,
      },
      transformResponse: [
        function (responseData: any) {
          try {
            return JSON.parse(responseData);
          } catch (e) {
            throw new ParseError(e.message);
          }
        },
      ],
    });
  } catch (e: any) {
    for (const key in e.response?.data) {
      if (e.response?.data.hasOwnProperty(key) && setError) {
        setError(key as any, {
          type: "custom",
          message: e.response?.data[key],
        });
      }
    }
    return e.response;
  }

  return response;
};

export const postApiFormData = async <T extends FieldValues>(data: {
  url: string;
  formData: FormData;
  setError?: UseFormSetError<T>;
}) => {
  await initInstance();
  const { url, formData, setError } = data;
  let response: any;
  try {
    response = await instance({
      method: "POST",
      url: `${url}`,
      data: formData,
      headers: {
        "Content-Type": `multipart/form-data`,
      },
      transformResponse: [
        function (responseData: any) {
          try {
            return JSON.parse(responseData);
          } catch (e) {
            throw new ParseError(e.message);
          }
        },
      ],
    });
  } catch (e: any) {
    for (const key in e.response?.data) {
      if (e.response?.data.hasOwnProperty(key) && setError) {
        setError(key as any, {
          type: "custom",
          message: e.response?.data[key],
        });
      }
    }
    return e.response;
  }

  return response;
};

export { getApiData, postApiData, patchApiData, deleteApiData };
